import React, { useEffect, useRef } from "react";
import {Vector3} from "../interfaces";

interface Player {
    steamId: string
    displayName: string
    position?: Vector3
    teamId?: number
    canvX?: number
    canvY?: number
}

interface Monument {
    displayName: string
    x: number
    z: number
}

interface MapCanvasProps {
    mapImageUrl: string
    mapSize: number
    players: Player[]
    monuments: Monument[]
    showMonuments?: boolean
    showPlayers?: boolean
    showPlayerNames?: boolean
    showPlayerOverlay: (steamId: string) => void;
    focusOnSteamId?: string
    focusUpdate?: number
}

interface TransformableContext extends CanvasRenderingContext2D {
    transformedPoint: (x: number, y: number) => DOMPoint;
    //getTransform: () => DOMMatrix;
}

interface CanvasEx extends HTMLCanvasElement {
    redraw: () => void
    redrawImage: () => void
    monuments?: Monument[]
    players?: Player[]
    showPlayerNames?: boolean
    mapSize: number
    mapImageUrl?: string
    focusPlayer: (steamId?: string) => void
}

export default function MapCanvas(props: MapCanvasProps) {
    const SCALE_FACTOR = 1.05;
    const MIN_ZOOM_LEVEL = 1;
    const MAX_ZOOM_LEVEL = 40;

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<CanvasEx>(null)

    const cursor = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
    const dragStart = useRef<{ x: number; y: number } | null>(null)
    const zoomLevel = useRef<number>(1)

    const highlightedPlayer = useRef<Player>()

    const debounceResizeRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d") as TransformableContext;
        if (!ctx) return;

        let map = new Image();
        map.onload = map.onerror = () => {
            redraw();
        };
        map.src = canvas.mapImageUrl!;

        cursor.current = {x: canvas.width / 2, y: canvas.height / 2};

        canvas.redrawImage = () => {
            map.src = canvas.mapImageUrl!;
        }

        canvas.redraw = () => {
            redraw();
        };

        canvas.focusPlayer = (steamId?: string) => {

            // If no steamId is provided, reset to default view
            if (!steamId) {
                return;
            }

            // Find the player with the given steamId
            const player = canvas.players?.find(p => p.steamId === steamId)
            if (!player || !player.canvX || !player.canvY) {
                console.warn("[MAP] Can not find player", steamId)
                return;
            }

            // Zoom all the way out
            zoom((zoomLevel.current - MIN_ZOOM_LEVEL) * -1);

            // Now zoom in
            cursor.current = {x: canvas.width / 2, y: canvas.height / 2};
            zoom(MAX_ZOOM_LEVEL - zoomLevel.current);

            // Move to the position on the map
            ctx.translate((player.canvX - (canvas.width * 0.5)) * -1, (player.canvY - (canvas.height * 0.5)) * -1);

            adjust()
            redraw(steamId)
        }

        const redraw = (steamId = "-1") => {
            // Clear the entire canvas
            let p1 = ctx.transformedPoint(0,0);
            let p2 = ctx.transformedPoint(canvas.width,canvas.height);

            ctx.clearRect(p1.x, p1.y, p2.x-p1.x, p2.y-p1.y);
            try{
                ctx.drawImage(map, 0, 0, canvas.width, canvas.height);
            }
            catch (e: any) {
                ctx.fillStyle = '#001d65'
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.font = `21px sans-serif`;
                ctx.textAlign = 'center';
                ctx.fillStyle = 'white';
                ctx.fillText(`Failed to load map image`, canvas.width / 2, canvas.height / 2);
                ctx.fillText(e.toString(), (canvas.width / 2), (canvas.height / 2) + 25, canvas.width - 50);
            }

            if (canvas.monuments){
                canvas.monuments.forEach(mon => {
                    drawMonument(mon.displayName, mon.x, mon.z);
                });
            }

            if (canvas.players){
                canvas.players.forEach(pl => {
                    drawPlayer(pl, pl.steamId === steamId);
                });
            }
        }

        //#region Event Handlers

        const onMouseLeave = (evt: MouseEvent) => {
            dragStart.current = null;
        }

        const onMouseDown = (evt: MouseEvent) => {
            document.body.style.userSelect = 'none';
            cursor.current = getMousePosition(evt);
            dragStart.current = ctx.transformedPoint(cursor.current.x, cursor.current.y);
            handleCursor(true);
        }

        const onMouseMove = (evt: MouseEvent) => {
            cursor.current = getMousePosition(evt);
            let pt = ctx.transformedPoint(cursor.current.x, cursor.current.y);
            let player = playerAt(pt.x, pt.y);
            if (dragStart.current){
                ctx.translate(pt.x - dragStart.current.x, pt.y - dragStart.current.y);
                adjust();
            }
            if (dragStart.current || (player !== highlightedPlayer.current)){
                highlightedPlayer.current = player
                handleCursor(!!dragStart.current);
                redraw(player?.steamId ?? "-1");
            }
        }

        const onMouseUp = (e: MouseEvent) => {
            dragStart.current = null;
            handleCursor();
        }

        const onClick = (e: MouseEvent) => {
            if (highlightedPlayer.current){
                props.showPlayerOverlay(highlightedPlayer.current.steamId)
            }
        }

        const onWheel = (evt: WheelEvent) => {
            let delta = evt.deltaY ? -evt.deltaY/50 : evt.detail ? -evt.detail : 0;
            if (delta) zoom(delta);

            evt.preventDefault()
        }

        canvas.addEventListener('mouseleave', onMouseLeave);
        canvas.addEventListener('mousedown', onMouseDown);
        canvas.addEventListener('mousemove', onMouseMove);
        canvas.addEventListener('mouseup', onMouseUp);
        canvas.addEventListener('click', onClick);
        canvas.addEventListener('wheel', onWheel);

        //#endregion

        const zoom = function(clicks: number) {
            if (zoomLevel.current + clicks < MIN_ZOOM_LEVEL) {
                clicks = MIN_ZOOM_LEVEL - zoomLevel.current;
            }
            if (zoomLevel.current + clicks > MAX_ZOOM_LEVEL) {
                clicks = MAX_ZOOM_LEVEL - zoomLevel.current;
            }

            if (clicks === 0){
                return
            }

            let factor = Math.pow(SCALE_FACTOR, clicks);
            let pt = ctx.transformedPoint(cursor.current.x, cursor.current.y);

            ctx.translate(pt.x, pt.y);
            ctx.scale(factor, factor);
            ctx.translate(-pt.x, -pt.y);

            adjust();

            zoomLevel.current += clicks;

            handleCursor();
            redraw();
        }

        const adjust = () => {
            let transX = 0, transY = 0;

            let pt = ctx.transformedPoint(0, 0);
            if(pt.x < 0) transX = pt.x;
            if(pt.y < 0) transY = pt.y;
            if(transX !== 0 || transY !== 0) {
                ctx.translate(transX, transY);
            }

            transX = transY = 0;
            pt = ctx.transformedPoint(canvas.width, canvas.height);
            if(pt.x > canvas.width) transX = pt.x - canvas.width;
            if(pt.y > canvas.height) transY = pt.y - canvas.height;
            if(transX !== 0 || transY !== 0) {
                ctx.translate(transX, transY);
            }
        }

        const handleCursor = (grabbing: boolean = false) => {
            if (!!highlightedPlayer.current){
                canvas.style.cursor = 'pointer';
            }
            else if (grabbing && zoomLevel.current !== MIN_ZOOM_LEVEL){
                canvas.style.cursor = 'grabbing';
            }
            else{
                if (zoomLevel.current === MIN_ZOOM_LEVEL){
                    canvas.style.cursor = 'default';
                }
                else{
                    canvas.style.cursor = 'grab';
                }
            }
        }

        const drawMonument = (name: string, x: number, z: number) => {
            let factor = canvas.width / canvas.mapSize;
            let sizeFactor = 1 - ((zoomLevel.current - 1) / MAX_ZOOM_LEVEL) * 0.6
            const canvX = (x + canvas.mapSize * 0.5) * factor;
            const canvY = (z *-1 + canvas.mapSize * 0.5) * factor;

            ctx.font = `${16 * sizeFactor}px PermanentMarker, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillStyle = 'black';
            ctx.fillText(name, canvX, canvY);
        }

        const drawPlayer = (player: Player, highlight: boolean) => {

            if (!player.position){
                return
            }

            let factor = canvas.width / canvas.mapSize;
            let sizeFactor = 1 - ((zoomLevel.current - 1) / MAX_ZOOM_LEVEL) * 0.6
            let radius = (20 * factor) * sizeFactor;

            player.canvX = (player.position.x + canvas.mapSize * 0.5) * factor;
            player.canvY = (player.position.z *-1 + canvas.mapSize * 0.5) * factor;
            //console.warn("draw player", player.displayName, player.canvX, player.canvY, "ms", canvas.mapSize)

            ctx.beginPath();
            ctx.strokeStyle = 'black';
            ctx.fillStyle = highlight ? '#ff00f5' : '#ff0000' //TODO getTeamColor(player.teamId)
            ctx.lineWidth = highlight ? 0.000001 : 1.5 * sizeFactor;
            ctx.arc(player.canvX, player.canvY, radius, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();

            if (highlight || canvas.showPlayerNames){
                ctx.font = `${20 * sizeFactor}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.fillStyle = 'white';
                ctx.fillText(player.displayName, player.canvX, player.canvY - radius * 2);
            }
        }

        function playerAt(x: number, y: number) {
            if (!canvas){
                return;
            }

            let playerRadius = (20 * canvas.width / canvas.mapSize) * (1 - ((zoomLevel.current - 1) / MAX_ZOOM_LEVEL) * 0.6)
            if (canvas.players){
                for (let player of canvas.players) {
                    let px = player.canvX, py = player.canvY;
                    if (px && py && x < px + playerRadius && x > px - playerRadius && y < py + playerRadius && y > py - playerRadius){
                        return player;
                    }
                }
            }

            return;
        }

        function getMousePosition(e: MouseEvent){
            let x = 0, y = 0
            if (canvas){
                x = e.offsetX * canvas.width / canvas.clientWidth
                y = e.offsetY * canvas.height / canvas.clientHeight
            }
            return { x: x, y: y }
        }

        const resizeCanvas = () => {
            if (!canvasRef.current) return;

            const containerRef = canvasRef.current.parentElement;
            if (!containerRef) return;

            const containerWidth = containerRef.offsetWidth;
            const containerHeight = containerRef.offsetHeight;
            const newSize = Math.min(containerWidth, containerHeight); // Keep square dimensions

            if (canvasRef.current){
                canvasRef.current.width = newSize
                canvasRef.current.height = newSize

                // Reset transforms & zoom level
                trackTransforms(ctx)
                zoomLevel.current = 1

                canvasRef.current.redraw()
            }
        };

        const resizeDebounced = () => {
            if (debounceResizeRef.current){
                clearTimeout(debounceResizeRef.current);
            }

            debounceResizeRef.current = setTimeout(resizeCanvas, 200);
        }

        console.log("[MAP] re-render component")
        resizeCanvas();
        window.addEventListener("resize", resizeDebounced);

        return () => {
            window.removeEventListener("resize", resizeDebounced);

            canvas.removeEventListener('mouseleave', onMouseLeave);
            canvas.removeEventListener('mousedown', onMouseDown);
            canvas.removeEventListener('mousemove', onMouseMove);
            canvas.removeEventListener('mouseup', onMouseUp);
            canvas.removeEventListener('click', onClick);
            canvas.removeEventListener('wheel', onWheel);
        };
    }, []); // This has to remain empty so we keep the original canvas

    useEffect(() => {
        if (!canvasRef.current){
            return;
        }
        console.log("[MAP] re-render from effect")

        if (props.players && props.showPlayers){
            canvasRef.current.players = props.players;
        }
        else{
            canvasRef.current.players = undefined;
        }
        if (props.monuments && props.showMonuments){
            canvasRef.current.monuments = props.monuments;
        }
        else{
            canvasRef.current.monuments = undefined;
        }
        canvasRef.current.showPlayerNames = props.showPlayerNames;
        canvasRef.current.redraw();
    }, [props.players, props.showPlayers, props.monuments, props.showMonuments, props.showPlayerNames]);

    useEffect(() => {
        if (!canvasRef.current) {
            return;
        }
        console.log("[MAP] re-render from effect 2")

        canvasRef.current.mapSize = props.mapSize;
        canvasRef.current.mapImageUrl = props.mapImageUrl;
        canvasRef.current.redrawImage();
    }, [props.mapImageUrl, props.mapSize]);

    useEffect(() => {
        if (!canvasRef.current){
            return
        }
        canvasRef.current.focusPlayer(props.focusOnSteamId);
    }, [props.focusOnSteamId, props.focusUpdate]);

    return (
        <div ref={containerRef} className="size-full flex items-start justify-center">
            <canvas ref={canvasRef} />
        </div>
    )
};


// Add transform tracking to the canvas context
const trackTransforms = (ctx: TransformableContext) => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    let xForm = svg.createSVGMatrix();
    const savedTransforms: DOMMatrix[] = [];

    const save = ctx.save.bind(ctx);
    ctx.save = () => {
        savedTransforms.push(xForm.translate(0, 0));
        save();
    };

    const restore = ctx.restore.bind(ctx);
    ctx.restore = () => {
        xForm = savedTransforms.pop() || svg.createSVGMatrix();
        restore();
    };

    const scale = ctx.scale.bind(ctx);
    ctx.scale = (sx, sy) => {
        xForm = xForm.scaleNonUniform(sx, sy);
        scale(sx, sy);
    };

    const translate = ctx.translate.bind(ctx);
    ctx.translate = (dx, dy) => {
        xForm = xForm.translate(dx, dy);
        translate(dx, dy);
    };

    const pt = svg.createSVGPoint();
    ctx.transformedPoint = (x, y) => {
        pt.x = x;
        pt.y = y;
        return pt.matrixTransform(xForm.inverse());
    };
};